// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState, useEffect } from 'react';
import { 
  Car, Search, ChevronLeft, ChevronRight, X,
  AlertCircle, Loader2, Ban, RotateCcw, Clock, Eye, AlertTriangle
} from 'lucide-react';
import api from '../../services/api';
import { VCIN_COLORS } from '../../styles/constants';

// Threat Level Badge
function ThreatBadge({ level }) {
  const colors = {
    0: { bg: '#E8F5E9', text: '#2E7D32' },
    1: { bg: '#FFF8E1', text: '#F57F17' },
    2: { bg: '#FFF3E0', text: '#E65100' },
    3: { bg: '#FFEBEE', text: '#C62828' },
    4: { bg: '#FFCDD2', text: '#B71C1C' },
    5: { bg: '#B71C1C', text: '#FFFFFF' }
  };
  const config = colors[level] || colors[0];
  
  return (
    <span 
      className="px-2 py-1 rounded text-xs font-semibold"
      style={{ backgroundColor: config.bg, color: config.text }}
    >
      Level {level}
    </span>
  );
}

// Suppressed Badge
function SuppressedBadge() {
  return (
    <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-200 text-gray-600">
      SUPPRESSED
    </span>
  );
}

// Table Skeleton
function TableSkeleton() {
  return (
    <tbody className="divide-y divide-gray-100">
      {[...Array(8)].map((_, i) => (
        <tr key={i} className="animate-pulse">
          <td className="px-6 py-4"><div className="h-6 w-20 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-5 w-16 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-12 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-8 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-20 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-20 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-8 w-24 bg-gray-200 rounded" /></td>
        </tr>
      ))}
    </tbody>
  );
}

// Suppress Modal
function SuppressModal({ vehicle, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Reason is required to suppress a vehicle');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await api.patch(`/admin/vehicles/${vehicle.id}/suppress`, { reason: reason.trim() });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to suppress vehicle');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-xl shadow-2xl z-50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <Ban className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Suppress Vehicle</h2>
            <p className="text-sm text-gray-500">VRN: {vehicle.vrn}</p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">This action will:</p>
              <ul className="list-disc list-inside mt-1 text-amber-700">
                <li>Remove this vehicle from threat calculations</li>
                <li>Grey out the vehicle in all views</li>
                <li>Write an audit log entry</li>
              </ul>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason for Suppression *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 resize-none"
              rows={3}
              placeholder="Enter the reason for suppressing this vehicle..."
              data-testid="suppress-reason"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-10 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-10 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="confirm-suppress-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
              Suppress
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

// Vehicle History Panel
function VehicleHistoryPanel({ vehicleId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await api.get(`/admin/vehicles/${vehicleId}/history`);
        setData(response.data);
      } catch (err) {
        console.error('Failed to load history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [vehicleId]);

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Threat History</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {loading ? (
          <div className="p-6 space-y-4 animate-pulse">
            <div className="h-6 w-32 bg-gray-200 rounded" />
            <div className="h-4 w-24 bg-gray-200 rounded" />
            <div className="space-y-3">
              {[1,2,3].map(i => (
                <div key={i} className="h-20 bg-gray-100 rounded-lg" />
              ))}
            </div>
          </div>
        ) : data ? (
          <div className="p-6">
            {/* Vehicle Summary */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <span 
                  className="px-3 py-1.5 rounded text-lg font-bold"
                  style={{ 
                    backgroundColor: VCIN_COLORS.plateYellow,
                    fontFamily: 'monospace'
                  }}
                >
                  {data.vehicle.vrn}
                </span>
                <ThreatBadge level={data.vehicle.threat_level} />
                {data.vehicle.suppressed && <SuppressedBadge />}
              </div>
              <p className="text-sm text-gray-500">
                Threat Score: {data.vehicle.threat_score}
              </p>
            </div>

            {/* Event Timeline */}
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Sighting History ({data.history?.length || 0})
            </h3>

            {data.history?.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No sighting history</p>
            ) : (
              <div className="space-y-3">
                {data.history?.map((event, idx) => (
                  <div 
                    key={idx} 
                    className="p-4 bg-gray-50 rounded-lg border-l-4"
                    style={{ borderColor: VCIN_COLORS.primary }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-xs font-mono" style={{ color: VCIN_COLORS.primary }}>{event.vcin_reference}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(event.datetime).toLocaleDateString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric'
                        })}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      {event.category?.replace(/_/g, ' ')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {event.site_name} • {event.company_name}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Officer: {event.officer_name}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">Failed to load history</div>
        )}
      </div>
    </>
  );
}

export default function SAVehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [suppressModal, setSuppressModal] = useState(null);
  const [historyPanel, setHistoryPanel] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchVehicles = async (page = 1, vrn = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 20 });
      if (vrn) params.append('vrn', vrn);
      
      const response = await api.get(`/admin/vehicles?${params.toString()}`);
      setVehicles(response.data.vehicles);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(search);
    fetchVehicles(1, search);
  };

  const handleUnsuppress = async (vehicleId) => {
    try {
      setActionLoading(vehicleId);
      await api.patch(`/admin/vehicles/${vehicleId}/unsuppress`);
      await fetchVehicles(pagination.page, searchQuery);
    } catch (err) {
      alert(err.message || 'Failed to unsuppress vehicle');
    } finally {
      setActionLoading(null);
    }
  };

  if (error && !loading) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div data-testid="sa-vehicles-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Vehicles</h1>
        <div className="text-sm text-gray-500">
          {pagination.total} vehicles in database
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by VRN..."
            value={search}
            onChange={(e) => setSearch(e.target.value.toUpperCase())}
            className="w-full h-10 pl-10 pr-24 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2"
            data-testid="vehicle-search"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 text-sm font-medium text-white rounded-md"
            style={{ backgroundColor: VCIN_COLORS.primary }}
          >
            Search
          </button>
        </div>
        {searchQuery && (
          <button
            type="button"
            onClick={() => { setSearch(''); setSearchQuery(''); fetchVehicles(1, ''); }}
            className="mt-2 text-sm text-gray-500 hover:text-gray-700"
          >
            Clear search
          </button>
        )}
      </form>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="vehicles-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">VRN</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Threat</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Score</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Sightings</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Regions</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">First Seen</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Seen</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            
            {loading ? (
              <TableSkeleton />
            ) : vehicles.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan="8" className="p-12 text-center">
                    <Car className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium text-gray-600">No vehicles found</p>
                    {searchQuery && (
                      <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
                    )}
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="divide-y divide-gray-100">
                {vehicles.map((vehicle) => (
                  <tr 
                    key={vehicle.id} 
                    className={`hover:bg-gray-50 transition-colors ${vehicle.suppressed ? 'bg-gray-50 opacity-60' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span 
                          className="px-2 py-1 rounded text-sm font-bold"
                          style={{ 
                            backgroundColor: vehicle.suppressed ? '#E5E7EB' : VCIN_COLORS.plateYellow,
                            fontFamily: 'monospace',
                            color: vehicle.suppressed ? '#6B7280' : '#000'
                          }}
                        >
                          {vehicle.vrn}
                        </span>
                        {vehicle.suppressed && <SuppressedBadge />}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <ThreatBadge level={vehicle.threat_level} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{vehicle.threat_score}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{vehicle.national_sightings}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1 max-w-32">
                        {vehicle.regions?.slice(0, 3).map((region, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">
                            {region.replace(/_/g, ' ').substring(0, 8)}
                          </span>
                        ))}
                        {vehicle.regions?.length > 3 && (
                          <span className="text-xs text-gray-400">+{vehicle.regions.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {vehicle.first_seen 
                        ? new Date(vehicle.first_seen).toLocaleDateString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric'
                          })
                        : '-'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {vehicle.last_seen 
                        ? new Date(vehicle.last_seen).toLocaleDateString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric'
                          })
                        : '-'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setHistoryPanel(vehicle.id)}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg hover:opacity-80"
                          style={{ color: VCIN_COLORS.primary, backgroundColor: `${VCIN_COLORS.primary}10` }}
                          data-testid={`view-history-${vehicle.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          History
                        </button>
                        
                        {vehicle.suppressed ? (
                          <button
                            onClick={() => handleUnsuppress(vehicle.id)}
                            disabled={actionLoading === vehicle.id}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 disabled:opacity-50"
                            data-testid={`unsuppress-${vehicle.id}`}
                          >
                            {actionLoading === vehicle.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <RotateCcw className="w-3.5 h-3.5" />
                            )}
                            Unsuppress
                          </button>
                        ) : (
                          <button
                            onClick={() => setSuppressModal(vehicle)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                            data-testid={`suppress-${vehicle.id}`}
                          >
                            <Ban className="w-3.5 h-3.5" />
                            Suppress
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 20 + 1} to {Math.min(pagination.page * 20, pagination.total)} of {pagination.total}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchVehicles(pagination.page - 1, searchQuery)}
                disabled={pagination.page === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-2">Page {pagination.page} of {pagination.pages}</span>
              <button
                onClick={() => fetchVehicles(pagination.page + 1, searchQuery)}
                disabled={pagination.page === pagination.pages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Suppress Modal */}
      {suppressModal && (
        <SuppressModal
          vehicle={suppressModal}
          onClose={() => setSuppressModal(null)}
          onSuccess={() => fetchVehicles(pagination.page, searchQuery)}
        />
      )}

      {/* History Panel */}
      {historyPanel && (
        <VehicleHistoryPanel
          vehicleId={historyPanel}
          onClose={() => setHistoryPanel(null)}
        />
      )}
    </div>
  );
}
